import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  useGetShopLevelRankingQuery,
  useCompleteLevelMutation,
} from "../features/levelRanking/levelRankingApiSlice";
import {
  agent as defaultAgent,
  tiers as defaultRetailTiers,
  pinnacle as defaultRetailPinnacle,
  stepper as defaultStepper,
  wholesalerTiers as defaultWholesaleTiers,
  wholesalerPinnacle as defaultWholesalePinnacle,
  manufacturerTiers as defaultManufacturerTiers,
  manufacturerPinnacle as defaultManufacturerPinnacle,
  rawmaterialTiers as defaultRawMaterialTiers,
  rawmaterialPinnacle as defaultRawMaterialPinnacle,
  servicesTiers as defaultServicesTiers,
  servicesPinnacle as defaultServicesPinnacle,
  transportTiers as defaultTransportTiers,
  transportPinnacle as defaultTransportPinnacle,
  foodTiers as defaultFoodTiers,
  foodPinnacle as defaultFoodPinnacle,
  companiesTiers as defaultCompaniesTiers,
  companiesPinnacle as defaultCompaniesPinnacle,
  propertyTiers as defaultPropertyTiers,
  propertyPinnacle as defaultPropertyPinnacle,
} from "../pages/Marketing_Area/Level_Ranking/data/jsondata.js";

const getInitials = (name) => {
  if (!name || typeof name !== "string") return "SE";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const useLevelRankingState = () => {
  const [isOpen, setIsOpen] = useState(false);

  // 1. Get logged-in user from Redux store (user or auth slice)
  const reduxName = useSelector((state) => state.user?.name);
  const authUser = useSelector((state) => state.auth?.user);

  const resolvedUser = useMemo(() => {
    const raw =
      (reduxName && typeof reduxName === "object" ? reduxName : null) ||
      authUser ||
      {};
    const fullname =
      raw.fullname ||
      (typeof reduxName === "string" && reduxName.trim() ? reduxName : null) ||
      raw.name ||
      raw.username ||
      raw.email?.split("@")[0] ||
      "Agent Partner";

    return {
      _id: raw._id || raw.id || null,
      fullname,
      email: raw.email || "",
      imageUrl: Array.isArray(raw.imageUrl)
        ? raw.imageUrl[0]
        : raw.imageUrl || "",
      initials: getInitials(fullname),
      isVerified: Boolean(raw.isVerified),
    };
  }, [reduxName, authUser]);

  const localUserId =
    (typeof window !== "undefined" &&
      (localStorage.getItem("userid") ||
        localStorage.getItem("id") ||
        localStorage.getItem("userId") ||
        localStorage.getItem("MeAsmarketerUserId"))) ||
    null;

  const userId = resolvedUser._id || localUserId;

  // 2. Active shop management
  const [selectedShopId, setSelectedShopId] = useState(() => {
    try {
      return localStorage.getItem("shopid") || "";
    } catch (_) {
      return "";
    }
  });

  // 3. RTK Query: fetch level ranking progression for the active shop / user
  const {
    data: apiResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetShopLevelRankingQuery(
    {
      shopId: selectedShopId || undefined,
      userId: userId || undefined,
    },
    {
      skip: !userId,
      refetchOnMountOrArgChange: true,
    }
  );

  // 4. Mutation to complete milestone level in real-time
  const [completeLevelMutation, { isLoading: isCompleting }] =
    useCompleteLevelMutation();

  const handleCompleteLevel1 = async (targetShopId) => {
    const shopToComplete =
      targetShopId || selectedShopId || apiResponse?.selectedShop?._id;
    if (!shopToComplete) return;
    try {
      await completeLevelMutation({
        shopId: shopToComplete,
        userId,
        level: 1,
      }).unwrap();
      refetch();
    } catch (err) {
      console.error("[handleCompleteLevel1] error:", err);
    }
  };

  // 5. Real-time updates without polling, without websockets, and without page reload
  useEffect(() => {
    const handleRealtimeUpdate = () => {
      refetch();
    };

    window.addEventListener(
      "selectezi:level_ranking_update",
      handleRealtimeUpdate
    );
    window.addEventListener(
      "selectezi:connection_updated",
      handleRealtimeUpdate
    );
    window.addEventListener("shop_invitation_accepted", handleRealtimeUpdate);

    let bc = null;
    if (typeof BroadcastChannel !== "undefined") {
      try {
        bc = new BroadcastChannel("selectezi_level_ranking");
        bc.onmessage = () => {
          refetch();
        };
      } catch (_) {}
    }

    const handleStorage = (e) => {
      if (
        e.key === "last_connection_action" ||
        e.key === "last_milestone_action" ||
        e.key === "shopid"
      ) {
        refetch();
      }
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        "selectezi:level_ranking_update",
        handleRealtimeUpdate
      );
      window.removeEventListener(
        "selectezi:connection_updated",
        handleRealtimeUpdate
      );
      window.removeEventListener(
        "shop_invitation_accepted",
        handleRealtimeUpdate
      );
      window.removeEventListener("storage", handleStorage);
      if (bc) {
        try {
          bc.close();
        } catch (_) {}
      }
    };
  }, [refetch]);

  // Sync selectedShopId if backend resolves a default shop and none was picked
  useEffect(() => {
    if (apiResponse?.selectedShop?._id && !selectedShopId) {
      setSelectedShopId(String(apiResponse.selectedShop._id));
    }
  }, [apiResponse, selectedShopId]);

  // 6. User profile, shops & selected shop resolution
  const user = useMemo(() => {
    return {
      fullname: apiResponse?.user?.fullname || resolvedUser.fullname,
      email: apiResponse?.user?.email || resolvedUser.email,
      initials: apiResponse?.user?.initials || resolvedUser.initials,
      imageUrl: apiResponse?.user?.imageUrl || resolvedUser.imageUrl,
      isVerified: apiResponse?.user?.isVerified ?? resolvedUser.isVerified,
    };
  }, [apiResponse, resolvedUser]);

  const shops = apiResponse?.shops || [];
  const selectedShop =
    apiResponse?.selectedShop || (shops.length > 0 ? shops[0] : null);

  const shopBusinessTypeNorm = String(selectedShop?.businessType || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  const detectedPlatformKey = useMemo(() => {
    if (apiResponse?.platform?.key) return apiResponse.platform.key;
    if (
      shopBusinessTypeNorm.includes("property") ||
      shopBusinessTypeNorm.includes("realestate")
    )
      return "property";
    if (
      shopBusinessTypeNorm.includes("company") ||
      shopBusinessTypeNorm.includes("companies") ||
      shopBusinessTypeNorm.includes("corporate") ||
      shopBusinessTypeNorm.includes("job")
    )
      return "companies";
    if (
      shopBusinessTypeNorm.includes("transport") ||
      shopBusinessTypeNorm.includes("vehicle") ||
      shopBusinessTypeNorm.includes("fleet")
    )
      return "transport";
    if (
      shopBusinessTypeNorm.includes("food") ||
      shopBusinessTypeNorm.includes("restaurant") ||
      shopBusinessTypeNorm.includes("cafe") ||
      shopBusinessTypeNorm.includes("catering")
    )
      return "food";
    if (shopBusinessTypeNorm.includes("rawmaterial")) return "rawmaterial";
    if (
      shopBusinessTypeNorm.includes("manufactur") ||
      shopBusinessTypeNorm.includes("industrial")
    )
      return "manufacturer";
    if (
      shopBusinessTypeNorm.includes("service") ||
      shopBusinessTypeNorm.includes("consultan") ||
      shopBusinessTypeNorm.includes("repair")
    )
      return "services";
    if (
      shopBusinessTypeNorm.includes("whole") ||
      shopBusinessTypeNorm.includes("wholeseller")
    )
      return "wholesaler";
    return "retail";
  }, [apiResponse, shopBusinessTypeNorm]);

  const activePlatform = useMemo(() => {
    if (apiResponse?.platform) return apiResponse.platform;

    const platformDescriptions = {
      property: {
        key: "property",
        name: "11. Property Platform",
        description:
          "Property businesses often deal with agents, buyers, sellers, landlords, tenants, developers, and investors. Because property transactions can take longer and happen less frequently, the proposed client targets are lower than retail and food.",
      },
      companies: {
        key: "companies",
        name: "10. Companies Platform",
        description:
          "This category covers general companies that do not fit neatly into a more specific business domain. Their potential audience depends on the company's industry, services, and target market, so the proposed thresholds use a moderate baseline.",
      },
      transport: {
        key: "transport",
        name: "8. Transport Platform",
        description:
          "Transport businesses may focus on logistics companies, fleet operators, freight customers, drivers, suppliers, and businesses requiring transportation. Their networks are specialised, so the proposed targets are relatively low.",
      },
      food: {
        key: "food",
        name: "9. Food Platform",
        description:
          "Food businesses can serve individual customers, restaurants, retailers, wholesalers, catering companies, and suppliers. The proposed targets are higher than most specialised business categories because food businesses can have frequent customer interactions.",
      },
      rawmaterial: {
        key: "rawmaterial",
        name: "6. Raw Material Platform",
        description:
          "Raw material suppliers serve specialised buyers, including manufacturers, factories, wholesalers, and other production businesses. Because their potential customer pool can be narrower, these targets are lower.",
      },
      manufacturer: {
        key: "manufacturer",
        name: "5. Manufacturer Platform",
        description:
          "Manufacturers often target distributors, wholesalers, retailers, commercial buyers, and raw material suppliers. Their business development cycles can be longer, so the proposed client targets are lower than retail.",
      },
      services: {
        key: "services",
        name: "7. Services Platform",
        description:
          "This category includes professional services, repair services, consulting, maintenance, design, and other service businesses. Agents need to build relevant customer relationships and maintain a strong service reputation.",
      },
      wholesaler: {
        key: "wholesaler",
        name: "4. Wholesaler Platform",
        description:
          "Wholesalers generally focus on retailers, bulk buyers, distributors, and other business customers. Their networks may be smaller than retail networks, but individual relationships can have greater commercial value.",
      },
      retail: {
        key: "retail",
        name: "3. Retail Platform",
        description:
          "Retail has the highest proposed requirements because retailers can connect with a broad mix of businesses and individual customers.",
      },
    };

    return (
      platformDescriptions[detectedPlatformKey] || platformDescriptions.retail
    );
  }, [apiResponse, detectedPlatformKey]);

  const metrics = useMemo(() => {
    if (apiResponse?.metrics) return apiResponse.metrics;
    return {
      totalClients: defaultAgent.clients,
      totalConnect: defaultAgent.connect,
      averageRating: defaultAgent.rating,
      totalRatings: 1,
      activeRegions: 1,
      isVerified: true,
    };
  }, [apiResponse]);

  const progression = useMemo(() => {
    if (apiResponse?.progression) return apiResponse.progression;

    let fallbackTiers = defaultRetailTiers;
    let fallbackPinnacle = defaultRetailPinnacle;

    if (detectedPlatformKey === "property") {
      fallbackTiers = defaultPropertyTiers;
      fallbackPinnacle = defaultPropertyPinnacle;
    } else if (detectedPlatformKey === "companies") {
      fallbackTiers = defaultCompaniesTiers;
      fallbackPinnacle = defaultCompaniesPinnacle;
    } else if (detectedPlatformKey === "transport") {
      fallbackTiers = defaultTransportTiers;
      fallbackPinnacle = defaultTransportPinnacle;
    } else if (detectedPlatformKey === "food") {
      fallbackTiers = defaultFoodTiers;
      fallbackPinnacle = defaultFoodPinnacle;
    } else if (detectedPlatformKey === "manufacturer") {
      fallbackTiers = defaultManufacturerTiers;
      fallbackPinnacle = defaultManufacturerPinnacle;
    } else if (detectedPlatformKey === "rawmaterial") {
      fallbackTiers = defaultRawMaterialTiers;
      fallbackPinnacle = defaultRawMaterialPinnacle;
    } else if (detectedPlatformKey === "services") {
      fallbackTiers = defaultServicesTiers;
      fallbackPinnacle = defaultServicesPinnacle;
    } else if (detectedPlatformKey === "wholesaler") {
      fallbackTiers = defaultWholesaleTiers;
      fallbackPinnacle = defaultWholesalePinnacle;
    }

    const t3 = fallbackTiers[2] || fallbackTiers[0];

    // Fallback baseline when query is pending
    return {
      completedLevelsCount: 1,
      isLevel1Completed: true,
      currentLevel: 2,
      currentTierLabel: defaultAgent.tierLabel,
      activeCommissionRate: defaultAgent.commission,
      nextTargetTier: {
        id: t3.id || 3,
        name: t3.name || "Gold Agent",
        clientsRequired: parseInt(t3.clients, 10) || 60,
        connectRequired: parseInt(t3.connect, 10) || 120,
        ratingRequired: 4.2,
        benefits: t3.benefits || [
          "Regional Expansion Hub Activated",
          "Priority Agent Desk Concierge & Directory Badge",
        ],
        clientsRemaining: Math.max(0, (parseInt(t3.clients, 10) || 60) - 25),
        connectRemaining: Math.max(0, (parseInt(t3.connect, 10) || 120) - 50),
        progressPercent: 75,
        prevTierThreshold: parseInt(fallbackTiers[1]?.clients, 10) || 30,
      },
      stepper: defaultStepper,
      tiers: fallbackTiers,
      pinnacle: fallbackPinnacle,
    };
  }, [apiResponse, detectedPlatformKey]);

  const hasLoadedData = Boolean(apiResponse);
  const isInitialLoading =
    isLoading ||
    (!hasLoadedData && isFetching) ||
    (!hasLoadedData && !error && Boolean(userId));

  const handleSelectShop = (shopId) => {
    setSelectedShopId(shopId);
    try {
      localStorage.setItem("shopid", shopId);
    } catch (_) {}
  };

  return {
    isOpen,
    setIsOpen,
    user,
    shops,
    selectedShop,
    selectedShopId,
    metrics,
    progression,
    activePlatform,
    isLoading,
    isFetching,
    isInitialLoading,
    hasLoadedData,
    error,
    isCompleting,
    handleSelectShop,
    handleCompleteLevel1,
    refetch,
  };
};

export default useLevelRankingState;

